import React, { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../../providers';
import { useSubscriptionTier } from '../../hooks/auth/useSubscriptionTier';
import { ProfileTopNav } from '../../components/Profile/ProfileTopNav';
import { PremiumModal } from '../../components/Premium';
import { PersonaChatRoomCard } from '../../components/CommunityChatRooms/PersonaChatRoomCard';
import { CommunityGuidelinesModal } from '../../components/CommunityChatRooms/CommunityGuidelinesModal';
import { CreateRoomModal } from '../../components/CommunityChatRooms/CreateRoomModal';
import { ChatRoomList } from '../../components/CommunityChatRooms/ChatRoomList';
import { PERSONA_MAP } from '../../utils/personaUtils';
import { CommunityService } from '../../services/Community/communityService';
import { profileService } from '../../services/Profile/profileService';
import { CommunityInfo } from '../../types/community';
import {
  PageContainer,
  PageContent,
  PageLoading,
  LoadingSpinner,
  LoadingText,
} from '../../styles/Profile/ProfilePages.styles';
import {
  CommunityContainer,
  HeaderSection,
  Title,
  Subtitle,
  TabsContainer,
  TabButton,
  RoomsGrid,
  CreateRoomButton,
} from '../../styles/CommunityChatRooms/CommunityChatRooms.styles';

type TabType = 'personas' | 'all';

export const CommunityChatRooms: React.FC = () => {
  const { user, loading: authLoading } = useAuthContext();
  // Creating a room is Premium Plus only. Read from the subscription rather
  // than the JWT role claim, so someone who upgrades can create a room without
  // logging out and back in first.
  const { isPremiumPlus } = useSubscriptionTier();
  // Lands on All Rooms: it is the first tab, and the custom rooms are where
  // new activity actually appears.
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [communities, setCommunities] = useState<CommunityInfo[]>([]);
  const [userPersona, setUserPersona] = useState<string | null>(null);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [isCreateRoomModalOpen, setIsCreateRoomModalOpen] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);

  useEffect(() => {
    profileService.getCommunityGuidelinesAccepted()
      .then((accepted) => setShowGuidelines(!accepted))
      .catch((error) => {
        console.error('Error checking community guidelines acceptance:', error);
        setShowGuidelines(true);
      });
  }, []);

  const handleAgreeGuidelines = async () => {
    // Only dismiss the modal once the backend confirms it saved — the modal
    // itself surfaces an inline error and lets the user retry on failure.
    await profileService.acceptCommunityGuidelines();
    setShowGuidelines(false);
  };

  useEffect(() => {
    if (user) {
      // Get user's persona from metadata
      const persona = user.user_metadata?.primary_persona || null;
      setUserPersona(persona);
    }
  }, [user]);

  const loadCommunities = useCallback(async () => {
    try {
      const data = await CommunityService.getAllCommunities();
      setCommunities(data);
    } catch (error) {
      console.error('Error loading chat rooms:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCommunities();
  }, [loadCommunities]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join me in Plenty of Hearts Community Chat Rooms!',
        text: 'Connect with like-minded people in persona-based chat rooms',
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleCreateRoom = () => {
    if (isPremiumPlus) {
      setIsCreateRoomModalOpen(true);
    } else {
      setIsPremiumModalOpen(true);
    }
  };

  const handlePremium = () => {
    setIsPremiumModalOpen(true);
  };

  const handleRoomCreated = async (name: string, description: string, icon: string) => {
    await CommunityService.createCommunity(name, description, icon);
    setIsCreateRoomModalOpen(false);
    await loadCommunities();
  };

  if (authLoading || isLoading) {
    return (
      <PageContainer>
        <PageLoading>
          <LoadingSpinner />
          <LoadingText>Loading chat rooms...</LoadingText>
        </PageLoading>
      </PageContainer>
    );
  }

  // Built from every community, including the persona ones: the Persona Rooms
  // tab reads its counts from here.
  const memberCountBySlug = new Map(communities.map((c) => [c.slug, c.memberCount]));

  // The six persona rooms have their own tab, which renders them from
  // PERSONA_MAP. Listing them here as well showed the same room twice in the
  // UI. Filtered rather than deleted: the persona rows are real communities —
  // they hold the posts, and the member counts above come from them.
  const customCommunities = communities.filter((community) => community.type !== 'persona');

  return (
    <PageContainer>
      <ProfileTopNav
        title="Community Chat Rooms"
        isOwnProfile={true}
        onShare={handleShare}
        onPremium={handlePremium}
      />

      <PageContent>
        <CommunityContainer>
          <HeaderSection>
            <Title>Connect with Your Community</Title>
            <Subtitle>
              Join persona-based chat rooms to meet like-minded people and build meaningful connections
            </Subtitle>
          </HeaderSection>

          <TabsContainer>
            <TabButton
              $isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            >
              All Rooms
            </TabButton>
            <TabButton
              $isActive={activeTab === 'personas'}
              onClick={() => setActiveTab('personas')}
            >
              Persona Rooms
            </TabButton>
          </TabsContainer>

          {activeTab === 'personas' ? (
            <RoomsGrid>
              {Object.values(PERSONA_MAP).map((persona) => (
                <PersonaChatRoomCard
                  key={persona.id}
                  persona={persona}
                  isUserPersona={userPersona === persona.id}
                  memberCount={memberCountBySlug.get(persona.id) ?? 0}
                />
              ))}
            </RoomsGrid>
          ) : (
            <>
              <ChatRoomList communities={customCommunities} />
              <CreateRoomButton onClick={handleCreateRoom}>
                <span>+</span> Create Chat Room
                {!isPremiumPlus && <span style={{ opacity: 0.75, fontSize: '0.85em' }}> · Premium Plus</span>}
              </CreateRoomButton>
            </>
          )}
        </CommunityContainer>
      </PageContent>

      {/* Premium Modal */}
      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
      />

      {/* Create Room Modal */}
      <CreateRoomModal
        isOpen={isCreateRoomModalOpen}
        onClose={() => setIsCreateRoomModalOpen(false)}
        onCreate={handleRoomCreated}
      />

      {/* Community Guidelines Modal */}
      <CommunityGuidelinesModal
        isOpen={showGuidelines}
        onAgree={handleAgreeGuidelines}
      />
    </PageContainer>
  );
};
